/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState, useEffect, useCallback } from "react";
import styled, { css } from "styled-components";
import Text from "./Text";
import Icon from "./Icon";
import Flex from "./Flex";
import Stack from "./Stack";
import { buttonBaseStyles } from "./Button";
import { useTranslate } from "../../hooks/useTranslate";

const ToastContainer = styled.div`
  position: fixed;
  top: 2rem;
  right: 2rem;
  max-width: 480px;
  z-index: 9999;

  ${({ theme }) => theme.containerMedia.sm} {
    inset-inline: 0;
    margin: auto;
    width: fit-content;
    height: fit-content;
  }
`;

const ToastItem = styled.div`
  position: relative;
  display: flex;
  padding: 0.5rem 0.75rem;
  ${({ $variant }) => variantStyles[$variant]};
  border-radius: 8px;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  box-shadow: 0 4px 4px 0 rgba(0, 32, 51, 0.04);
  background: rgba(${({ theme }) => theme.colors.surface.rgba});

  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  top: ${({ $isVisible }) => ($isVisible ? 0 : "5px")};
  transition: opacity 0.2s ease-in-out, top 0.2s ease-in-out;

  @starting-style {
    opacity: 0;
    top: 5px;
  }
`;

const ToastIconWrapper = styled.span`
  flex-shrink: 0;
`;

const ToastCloseButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  ${buttonBaseStyles}
`;

const variantStyles = {
  success: css`
    color: rgba(${({ theme }) => theme.colors.success.rgba});
    &:before {
      background: rgba(${({ theme }) => theme.colors.success.rgba});
    }
  `,
  error: css`
    color: rgba(${({ theme }) => theme.colors.error.rgba});
    &:before {
      background: rgba(${({ theme }) => theme.colors.error.rgba});
    }
  `,
  warning: css`
    color: rgba(${({ theme }) => theme.colors.warning.rgba});
    &:before {
      background: rgba(${({ theme }) => theme.colors.warning.rgba});
    }
  `,
  info: css`
    color: rgba(${({ theme }) => theme.colors.text.rgba});
    &:before {
      background: rgba(${({ theme }) => theme.colors.border.rgba});
    }
  `,
};

const variantIcons = {
  success: <Icon.CheckCircle color="success" size={Icon.SIZE.MD} />,
  error: <Icon.Error color="danger" size={Icon.SIZE.MD} />,
  warning: <Icon.AlertCircle color="warning" size={Icon.SIZE.MD} />,
  info: <Icon.InfoCircle color="dark" size={Icon.SIZE.MD} />,
};

const Toast = ({ title, description, variant, duration = 3000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const { _t } = useTranslate();

  useEffect(() => {
    if (duration === 0) return;

    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <ToastItem $variant={variant} $isVisible={isVisible}>
      <Flex $gap="0.5rem" $align="center" $padding={duration === 0 ? `0 2rem 0 0` : ''}>
        <ToastIconWrapper>
          {variantIcons[variant]}
        </ToastIconWrapper>
        <Stack $gap="0.125rem">
          {title &&
            <Text weight="bold" color="dark">
              {title}
            </Text>
          }
          {description && <Text size="small" color="dark">{description}</Text>}
        </Stack>
        {duration === 0 && (
          <ToastCloseButton onClick={onClose} aria-label={_t("Close")}>
            <Icon.Cross
              size={Icon.SIZE.SM}
            />
          </ToastCloseButton>
        )}
      </Flex>
    </ToastItem>
  );
};

const ToastManager = () => {
  const [currentToast, setCurrentToast] = useState(null);

  const addToast = useCallback((toast) => {
    setCurrentToast(toast);
  }, []);

  const removeToast = useCallback(() => {
    setCurrentToast(null);
  }, []);

  useEffect(() => {
    window.toastManager = { addToast };

    return () => {
      delete window.toastManager;
    };
  }, [addToast]);

  return currentToast ? (
    <ToastContainer role="status" aria-live="polite">
      <Toast key={currentToast.id} {...currentToast} onClose={removeToast} />
    </ToastContainer>
  ) : null;
};

const useToast = () => {
  const toast = useCallback((toast) => {
    if (window.toastManager) {
      window.toastManager.addToast({ ...toast, id: Date.now() });
    } else {
      console.warn("ToastManager is not initialized yet.");
    }
  }, []);

  return { toast };
};

export { ToastManager, useToast };
